export type Easing = (t: number) => number;

export const Ease = {
  linear: (t: number) => t,
  quadIn: (t: number) => t * t,
  quadOut: (t: number) => t * (2 - t),
  quadInOut: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  cubicOut: (t: number) => 1 - Math.pow(1 - t, 3),
  backOut: (t: number) => {
    const c = 1.70158;
    return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
  },
  bounceOut: (t: number) => {
    const n = 7.5625;
    const d = 2.75;
    if (t < 1 / d) return n * t * t;
    if (t < 2 / d) return n * (t -= 1.5 / d) * t + 0.75;
    if (t < 2.5 / d) return n * (t -= 2.25 / d) * t + 0.9375;
    return n * (t -= 2.625 / d) * t + 0.984375;
  },
} satisfies Record<string, Easing>;

interface TweenItem {
  elapsed: number;
  duration: number;
  ease: Easing;
  onUpdate: (t: number) => void;
  resolve: () => void;
}

/**
 * Frame-driven tweens that can be awaited. Scenes own a Tweener and pump it
 * from `update`, so animations pause automatically when the scene does.
 */
export class Tweener {
  private items: TweenItem[] = [];

  /** Runs `onUpdate` with eased 0..1 for `duration` seconds. */
  run(duration: number, onUpdate: (t: number) => void, ease: Easing = Ease.linear): Promise<void> {
    if (duration <= 0) {
      onUpdate(1);
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.items.push({ elapsed: 0, duration, ease, onUpdate, resolve });
    });
  }

  /** Interpolates numeric properties on an object (positions, rotations…). */
  to<T extends object>(
    target: T,
    props: { [K in keyof T]?: number },
    duration: number,
    ease: Easing = Ease.quadInOut,
  ): Promise<void> {
    const bag = target as Record<string, number>;
    const keys = Object.keys(props);
    const start = keys.map((k) => bag[k]);
    return this.run(
      duration,
      (t) => {
        keys.forEach((k, i) => {
          const to = (props as Record<string, number>)[k];
          bag[k] = start[i] + (to - start[i]) * t;
        });
      },
      ease,
    );
  }

  /** Resolves after `seconds`, in scene time. */
  wait(seconds: number): Promise<void> {
    return this.run(seconds, () => {});
  }

  update(dt: number): void {
    if (this.items.length === 0) return;
    // Iterate a copy: a resolved tween usually queues the next one.
    const items = this.items;
    this.items = [];
    for (const item of items) {
      item.elapsed += dt;
      const raw = Math.min(1, item.elapsed / item.duration);
      item.onUpdate(item.ease(raw));
      if (raw >= 1) item.resolve();
      else this.items.push(item);
    }
  }

  /** Drops everything without resolving. Use when tearing a scene down. */
  clear(): void {
    this.items = [];
  }
}
